"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useHeaderMotion } from "@/components/motion/header-motion";
import IconLabel from "@/icons/IconLabel";
import { fittingNavigationCount } from "@/utils/navigation-overflow";
import { usePathname } from "next/navigation";
import { groupChildren, type NavEntry } from "@/utils/header-navigation";
import type { NavigationBreakpoint } from "@/hooks/useMinWidth";
import NavigationLink from "./NavigationLink";
import NavIcon from "./NavIcon";
import { useMenuFormatting, useMenuLayout } from "./NavigationFormatting";

const sameKeys = (a: string[], b: string[]) =>
  a.length === b.length && a.every((key, index) => key === b[index]);

function DropdownContent({
  entry,
  path,
  onNavigate,
}: {
  entry: NavEntry;
  path: string | null;
  onNavigate: () => void;
}) {
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  return groupChildren(entry).map((child) =>
    child.kind === "heading" ? (
      <div
        key={child.key}
        {...layoutFormat("dropdown")}
        className="re-nav-dropdown-section"
      >
        <p {...format("heading", child.id)} className="re-nav-dropdown-heading">
          {child.label}
        </p>
        {child.children.map((grandchild) => (
          <NavigationLink
            key={grandchild.key}
            link={grandchild.link!}
            {...format("grandchild", grandchild.id, "dropdown")}
            className="re-nav-dropdown-link"
            current={path === grandchild.link!.href}
            onClick={onNavigate}
          >
            <IconLabel iconId={grandchild.iconId}>{grandchild.label}</IconLabel>
          </NavigationLink>
        ))}
      </div>
    ) : (
      <NavigationLink
        key={child.key}
        link={child.link!}
        {...format("child", child.id, "dropdown")}
        className="re-nav-dropdown-link"
        current={path === child.link!.href}
        onClick={onNavigate}
      >
        <IconLabel iconId={child.iconId}>{child.label}</IconLabel>
      </NavigationLink>
    ),
  );
}

function MegaLink({
  nested = false,
  entry,
  path,
  onNavigate,
}: {
  entry: NavEntry;
  nested?: boolean;
  path: string | null;
  onNavigate: () => void;
}) {
  const format = useMenuFormatting();
  return (
    <NavigationLink
      {...format(nested ? "grandchild" : "child", entry.id, "mega")}
      link={entry.link!}
      className="re-nav-mega-link"
      current={path === entry.link!.href}
      onClick={onNavigate}
    >
      {entry.iconId !== null && (entry.icon || entry.iconId) && (
        <span className="re-nav-mega-icon">
          <NavIcon name={entry.icon} iconId={entry.iconId} />
        </span>
      )}
      <span className="re-nav-mega-text">
        <span className="re-nav-mega-label">{entry.label}</span>
        {entry.description && (
          <span
            {...format("description", entry.id)}
            className="re-nav-mega-description"
          >
            {entry.description}
          </span>
        )}
      </span>
    </NavigationLink>
  );
}

/** Loose links form one untitled column; every heading becomes its own column. */
function megaColumns(entry: NavEntry) {
  const columns: {
    key: string;
    id?: string;
    title?: string;
    links: NavEntry[];
  }[] = [];
  for (const child of groupChildren(entry)) {
    if (child.kind === "heading")
      columns.push({
        key: child.key,
        id: child.id,
        title: child.label,
        links: child.children,
      });
    else {
      const last = columns[columns.length - 1];
      if (last && !last.title) last.links.push(child);
      else columns.push({ key: child.key, links: [child] });
    }
  }
  return columns;
}

function MegaContent({
  entry,
  path,
  onNavigate,
}: {
  entry: NavEntry;
  path: string | null;
  onNavigate: () => void;
}) {
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  return (
    <div className="re-nav-mega-inner">
      {megaColumns(entry).map((column) => (
        <div
          key={column.key}
          {...layoutFormat("mega")}
          className="re-nav-mega-column"
        >
          {column.title && (
            <p
              {...format("heading", column.id)}
              className="re-nav-mega-heading"
            >
              {column.title}
            </p>
          )}
          {column.links.map((link) => (
            <MegaLink
              key={link.key}
              entry={link}
              nested={!!column.title}
              path={path}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Keep links mounted for stable measurement and animate before hiding them. */
function AnimatedSubmenu({
  expanded,
  children,
  ...props
}: {
  expanded: boolean;
  children: React.ReactNode;
} & Pick<React.HTMLAttributes<HTMLDivElement>, "id" | "className" | "style">) {
  const { motion, useReducedMotion } = useHeaderMotion();
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(expanded);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  useLayoutEffect(() => {
    if (expanded) setVisible(true);
  }, [expanded]);
  return (
    <motion.div
      {...props}
      initial={false}
      style={{
        ...props.style,
        visibility: expanded || visible ? "visible" : "hidden",
        pointerEvents: expanded ? "auto" : "none",
      }}
      animate={{ opacity: expanded ? 1 : 0, y: expanded || reduced ? 0 : -8 }}
      transition={{ duration: reduced ? 0 : 0.18 }}
      onAnimationComplete={() => {
        if (!expandedRef.current) setVisible(false);
      }}
      aria-hidden={!expanded || undefined}
      inert={!expanded || undefined}
    >
      {children}
    </motion.div>
  );
}

/**
 * The desktop bar. Groups open as a dropdown or a full-width mega panel; their links stay in the
 * markup while closed. With `overflow`, items that do not fit are hidden here and reported so the
 * panel can show them first.
 */
export default function NavigationBar({
  items,
  submenus,
  bp,
  overflow,
  onOverflowChange,
  className,
  align = "end",
  hoverEffect,
  hoverMarker,
  appearance,
  onFocusedOverflow,
}: {
  items: NavEntry[];
  submenus: "dropdown" | "mega";
  bp: NavigationBreakpoint;
  overflow: boolean;
  onOverflowChange: (keys: string[]) => void;
  className?: string;
  align?: "start" | "end" | "between";
  hoverEffect?: "chevron" | "underline" | "background" | "none";
  hoverMarker?: "slashes";
  appearance: "default" | "floating" | "classic";
  onFocusedOverflow?: () => void;
}) {
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  const id = useId();
  const path = usePathname();
  const [openKey, setOpenKey] = useState<string>();
  const [hidden, setHidden] = useState<string[]>([]);
  const [alignEnd, setAlignEnd] = useState<string>();
  const navRef = useRef<HTMLElement>(null);
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  // Hovering opens a group; the click that usually follows must not close it again.
  const hoverOpenedAt = useRef(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => setOpenKey(undefined), [path]);

  useEffect(() => onOverflowChange(hidden), [hidden, onOverflowChange]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      let next: string[] = [];
      if (overflow && getComputedStyle(nav).display !== "none") {
        const list = nav.firstElementChild as HTMLElement | null;
        const gap = list
          ? parseFloat(getComputedStyle(list).columnGap) || 0
          : 0;
        const count = fittingNavigationCount(
          items.map(
            (item) =>
              itemRefs.current.get(item.key)?.getBoundingClientRect().width ??
              0,
          ),
          nav.clientWidth,
          items.map((item) => {
            const value = parseFloat(
              getComputedStyle(
                itemRefs.current.get(item.key)!,
              ).getPropertyValue("--re-nav-item-gap"),
            );
            return Number.isFinite(value) ? value : gap;
          }),
        );
        next = items.slice(count).map((item) => item.key);
        if (
          next.some((key) =>
            itemRefs.current.get(key)?.contains(document.activeElement),
          )
        )
          onFocusedOverflow?.();
      }
      if (next.includes(openKey ?? "")) setOpenKey(undefined);
      setHidden((previous) => (sameKeys(previous, next) ? previous : next));
    };
    measure();
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(nav);
    if (nav.firstElementChild) observer.observe(nav.firstElementChild);
    itemRefs.current.forEach((element) => observer.observe(element));
    let disposed = false;
    document.fonts?.ready
      .then(() => {
        if (!disposed) schedule();
      })
      .catch(() => undefined);
    document.fonts?.addEventListener("loadingdone", schedule);
    window.addEventListener("resize", schedule);
    return () => {
      disposed = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
      document.fonts?.removeEventListener("loadingdone", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [items, overflow, hoverEffect, openKey, onFocusedOverflow]);

  const open = useCallback((key: string, element?: HTMLElement | null) => {
    clearTimeout(closeTimer.current);
    if (element)
      setAlignEnd(
        element.getBoundingClientRect().left + 280 > window.innerWidth
          ? key
          : undefined,
      );
    setOpenKey(key);
  }, []);
  const close = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpenKey(undefined);
  }, []);
  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenKey(undefined), 180);
  }, []);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!items.length) return null;
  return (
    <nav
      ref={navRef}
      className={`re-nav-bar${className ? ` ${className}` : ""}`}
      data-bp={bp}
      data-align={align}
      data-hover={hoverEffect}
      data-hover-marker={hoverMarker}
      data-appearance={appearance}
      data-overflow-mode={overflow ? "auto" : undefined}
      data-visible-count={items.length - hidden.length}
      aria-label="Hovedmenu"
    >
      <ul {...layoutFormat("bar")} className="re-nav-bar-list">
        {items.map((item) => {
          const submenu = item.children.length ? submenus : undefined;
          const expanded = openKey === item.key;
          const panelId = `${id}-${item.key}`;
          const toggle = (event: React.MouseEvent<HTMLElement>) => {
            if (!expanded)
              return open(item.key, event.currentTarget.closest("li"));
            if (Date.now() - hoverOpenedAt.current > 600) close();
          };
          return (
            <li
              key={item.key}
              ref={(element) => {
                if (element) itemRefs.current.set(item.key, element);
                else itemRefs.current.delete(item.key);
              }}
              className="re-nav-bar-item"
              data-submenu={submenu}
              data-open={expanded || undefined}
              data-overflow={hidden.includes(item.key) || undefined}
              inert={hidden.includes(item.key) ? true : undefined}
              data-align-end={alignEnd === item.key || undefined}
              onPointerEnter={(event) => {
                if (!submenu || event.pointerType !== "mouse") return;
                if (!expanded) hoverOpenedAt.current = Date.now();
                open(item.key, event.currentTarget);
              }}
              onPointerLeave={(event) =>
                submenu && event.pointerType === "mouse" && scheduleClose()
              }
              onBlur={(event) => {
                if (
                  expanded &&
                  !event.currentTarget.contains(event.relatedTarget as Node)
                )
                  close();
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape" && expanded) {
                  close();
                  event.currentTarget
                    .querySelector<HTMLElement>("[aria-expanded]")
                    ?.focus();
                }
              }}
            >
              {submenu || !item.link ? (
                <button
                  type="button"
                  {...format("bar", item.id)}
                  className="re-nav-bar-link re-nav-bar-trigger"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={toggle}
                >
                  {item.label}
                  <NavIcon name="chevron" className="re-nav-chevron" />
                </button>
              ) : (
                <NavigationLink
                  link={item.link}
                  {...format("bar", item.id)}
                  className="re-nav-bar-link"
                  current={path === item.link.href}
                  onClick={close}
                >
                  {item.label}
                </NavigationLink>
              )}
              {submenu && (
                <AnimatedSubmenu
                  expanded={openKey === item.key && !hidden.includes(item.key)}
                  id={panelId}
                  {...(submenu === "dropdown" ? layoutFormat("dropdown") : {})}
                  className={
                    submenu === "mega" ? "re-nav-mega" : "re-nav-dropdown"
                  }
                >
                  {submenu === "mega" ? (
                    <MegaContent entry={item} path={path} onNavigate={close} />
                  ) : (
                    <DropdownContent
                      entry={item}
                      path={path}
                      onNavigate={close}
                    />
                  )}
                </AnimatedSubmenu>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
